import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Container from "@/components/ui/container";
import Button from "@/components/ui/button";
import { DeleteConfirmationModal } from "@/components/ui/delete-confirmation-modal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/modules/auth";
import { CreateWidgetModal, WidgetEmptyPlaceholder, WidgetList, WidgetListSkeleton } from "@/modules/widget";
import WidgetGrid from "@/modules/widget/components/widget-grid";
import StatusChangeConfirmationModal from "@/modules/widget/components/status-change-confirmation-modal";
import { useWidgets } from "@/modules/widget/hooks/use-widgets";
import type { Widget } from "@/modules/widget/types";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useBrands } from "@/modules/brand";

type ViewMode = "list" | "grid";

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (mode: ViewMode) => void }) {
	return (
		<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
			<button
				type="button"
				onClick={() => onChange("grid")}
				className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${value === "grid" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
					/>
				</svg>
			</button>
			<button
				type="button"
				onClick={() => onChange("list")}
				className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${value === "list" ? "bg-white text-primary shadow-sm" : "text-tertiary hover:text-primary"}`}
			>
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
				</svg>
			</button>
		</div>
	);
}

export default function WidgetsPage() {
	const { widgets, templates, indexWidgets, updateWidget, deleteWidget, createWidget, listTemplates } = useWidgets();
	const { currentWorkspaceId } = useAuth();
	const { selectedBrand, viewBrand } = useBrands();
	const [loading, setLoading] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isStatusChanging, setIsStatusChanging] = useState(false);
	const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
	const [pendingStatusChange, setPendingStatusChange] = useState<{ id: string; currentStatus: "published" | "unpublished"; newStatus: "published" | "unpublished" } | null>(null);
	const [viewMode, setViewMode] = useState<ViewMode>("grid");
	const navigate = useNavigate();
	const { showToast } = useToast();

	useEffect(() => {
		if (!currentWorkspaceId) return;

		(async () => {
			setLoading(true);
			const [widgetResult] = await Promise.all([indexWidgets(currentWorkspaceId), !templates ? listTemplates() : Promise.resolve()]);
			setLoading(false);
			if (widgetResult?.data && widgetResult.data.length > 0) {
				const store = useOnboardingStore.getState();
				if (!store.completedSteps.includes("setup_widget")) {
					store.completeStep(currentWorkspaceId, "setup_widget");
				}
			}
		})();
	}, [currentWorkspaceId, indexWidgets, templates, listTemplates]);

	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrand(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrand]);

	const handleCreateWidget = () => {
		setCreateModalOpen(true);
	};

	const handleCreateWidgetSubmit = async (data: { title: string; template: string; template_version: number }): Promise<{ success: boolean; data?: { id: string } }> => {
		if (!currentWorkspaceId) {
			return { success: false };
		}

		const selectedTemplate = templates?.find((t) => t.template === data.template && t.version === data.template_version);
		if (!selectedTemplate) {
			showToast({
				title: "Failed to create widget",
				description: "Selected template not found. Please try again.",
				status: "error",
				duration: 5000,
			});
			return { success: false };
		}

		const result = await createWidget(currentWorkspaceId, {
			title: data.title,
			display_type: selectedTemplate.display_type,
			template: selectedTemplate.template,
			template_version: selectedTemplate.version,
		});

		if (result.success && result.data) {
			useOnboardingStore.getState().completeStep(currentWorkspaceId, "setup_widget");
			navigate(`/${currentWorkspaceId}/widgets/${result.data.id}/edit`, { state: { justCreated: true } });
			return { success: true, data: { id: result.data.id } };
		}

		showToast({
			title: "Failed to create widget",
			description: "Something went wrong. Please try again.",
			status: "error",
			duration: 5000,
		});

		return { success: false };
	};

	const handleEdit = (id: string) => {
		navigate(`/${currentWorkspaceId}/widgets/${id}/edit`);
	};

	const handleDelete = (id: string) => {
		const widget = widgets?.find((item) => item.id === id) || null;
		setSelectedWidget(widget);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (!currentWorkspaceId || !selectedWidget) return;
		setIsDeleting(true);
		const result = await deleteWidget(selectedWidget.id, currentWorkspaceId);
		setIsDeleting(false);
		setDeleteModalOpen(false);

		if (result.success) {
			showToast({
				title: "Widget deleted",
				description: "The widget has been deleted successfully.",
				status: "success",
				duration: 5000,
			});
			await indexWidgets(currentWorkspaceId);
		} else {
			showToast({
				title: "Failed to delete widget",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}
	};

	const handleStatusChange = (id: string, status: string) => {
		const widget = widgets?.find((item) => item.id === id);
		if (!widget) return;

		const currentStatus = widget.status as "published" | "unpublished";
		const newStatus = status as "published" | "unpublished";

		if (newStatus === "unpublished") {
			setPendingStatusChange({ id, currentStatus, newStatus });
			setStatusChangeModalOpen(true);
		} else {
			confirmStatusChange(id, newStatus);
		}
	};

	const confirmStatusChange = async (id: string, status: "published" | "unpublished") => {
		if (!currentWorkspaceId) return;
		setIsStatusChanging(true);
		const result = await updateWidget(id, currentWorkspaceId, { status });
		setIsStatusChanging(false);
		setStatusChangeModalOpen(false);
		setPendingStatusChange(null);

		if (result.success) {
			await indexWidgets(currentWorkspaceId);
			showToast({
				title: "Status updated",
				description: "The widget status has been updated.",
				status: "success",
				duration: 4000,
			});
		} else {
			showToast({
				title: "Failed to update status",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}
	};

	const hasWidgets = widgets && widgets.length > 0;
	const hasNoWidgets = !loading && widgets !== null && widgets.length === 0;

	return (
		<>
			{hasNoWidgets ? (
				<div className="py-12">
					<Container>
						<div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
							<h2 className="text-xl font-semibold text-primary mb-2">No widgets yet</h2>
							<p className="text-tertiary text-center max-w-md mb-8">Create embeddable widgets to showcase your testimonials and ratings.</p>
							<WidgetEmptyPlaceholder onClick={handleCreateWidget} />
						</div>
					</Container>
				</div>
			) : (
				<div className="space-y-6 py-12">
					<Container>
						{hasWidgets && (
							<div className="flex items-center justify-between mb-6">
								<div>
									<h1 className="text-xl font-semibold text-primary">Widgets</h1>
									<p className="mt-1 text-tertiary max-w-[640px]">Build embeddable widgets to show your best testimonials on any website.</p>
								</div>
								<div className="flex items-center gap-3">
									<ViewToggle value={viewMode} onChange={setViewMode} />
									<Button onClick={handleCreateWidget}>Create Widget</Button>
								</div>
							</div>
						)}

						{loading ? (
							<WidgetListSkeleton />
						) : widgets && viewMode === "list" ? (
							<WidgetList widgets={widgets} templates={templates} onDelete={handleDelete} onEdit={handleEdit} onStatusChange={handleStatusChange} userRole={selectedBrand?.role} />
						) : (
							widgets && <WidgetGrid widgets={widgets} templates={templates} onDelete={handleDelete} onEdit={handleEdit} onStatusChange={handleStatusChange} userRole={selectedBrand?.role} />
						)}
					</Container>
				</div>
			)}

			<CreateWidgetModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSuccess={() => {}}
				onSubmit={handleCreateWidgetSubmit}
			/>

			<DeleteConfirmationModal
				open={deleteModalOpen}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
				isLoading={isDeleting}
				title="Delete Widget"
				description={`Are you sure you want to delete "${selectedWidget?.title || "widget"}"? This action cannot be undone.`}
			/>

			{pendingStatusChange && (
				<StatusChangeConfirmationModal
					open={statusChangeModalOpen}
					onOpenChange={(open) => {
						setStatusChangeModalOpen(open);
						if (!open) setPendingStatusChange(null);
					}}
					currentStatus={pendingStatusChange.currentStatus}
					newStatus={pendingStatusChange.newStatus}
					onConfirm={() => confirmStatusChange(pendingStatusChange.id, pendingStatusChange.newStatus)}
					isLoading={isStatusChanging}
				/>
			)}
		</>
	);
}
